// PageMeta — declarative <head> injection using react-helmet-async.
//
// Renders a canonical URL, page title, meta description, and Open Graph +
// Twitter card tags. All pages use signdc.org as the canonical domain so
// preview URLs never leak into search results. Passing `noindex` renders
// the noindex/nofollow meta for admin / verify pages.
import { Helmet } from "react-helmet-async";

const CANONICAL_DOMAIN = "https://signdc.org";
const DEFAULT_OG_IMAGE = "https://signdc.org/og-image.jpg";
const SITE_NAME = "DOCM · Declaration of Consciousness Movement";

function normalizePath(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

export default function PageMeta({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  type = "website",
}) {
  // Guard against double-suffix — some pages pass a title that already ends
  // with ' · DOCM' (e.g. 'Ahimsa · The 9 Principles · DOCM'), which would
  // otherwise produce 'Ahimsa · … · DOCM · DOCM' after concatenation.
  const cleanTitle = (title || "").replace(/\s*[·|]\s*DOCM\s*$/i, "").trim();
  const fullTitle = cleanTitle ? `${cleanTitle} · DOCM` : SITE_NAME;
  const canonical = `${CANONICAL_DOMAIN}${normalizePath(path)}`;
  const desc = description || "Declaration of Consciousness Movement — turn awareness into action. Sign the Declaration, perform a Random Act of Consciousness, join the world on 11·11.";
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      {/* Twitter card — twitter:card is set once in /public/index.html for
          global reach (crawlers that don't run JS still see it). We only
          emit per-page overrides here. */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
